/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {memo} from 'react';
import {useSelector} from 'react-redux';
import {useSignals} from '@preact/signals-react/runtime';
import {
  DeviceActionDialogs as ApplicationsDeviceActionDialogs
} from '@ion/applications/src/pages/devices/DeviceActionDialogs';
import {
  devicesFiltersSelector,
  devicesIncludeSubAccountsSelector,
  devicesSearchSelector,
  devicesTotalRowsCountSelector
} from '@ion/applications/src/pages/devices/state-selectors';
import {CRUDPermissions, GenericObject} from '@ion/core/src/types/entities';
import {AssetTreeRefresh} from '@ion/dls/src/editors/AssetEditor';
import useModularSearch from '@/routes/provisioning/devicefiles/useModularSearch';
import CreateJobNotificationsBanner from '@/routes/provisioning/jobsearchengine/CreateJobNotificationsBanner';
import CreateJobNotificationsToggle from '@/routes/provisioning/jobsearchengine/CreateJobNotificationsToggle';
import {liveJobsEnabled} from '@/routes/provisioning/jobsearchengine/signals/liveUpdates';
import {useAutoWatchCreatedJob} from '@/routes/provisioning/jobsearchengine/useAutoWatchCreatedJob';

type DeviceActionDialogsProps = AssetTreeRefresh & {
  selectedJobId: (jobId: number) => void;
  isToggleSwitchGrid?: boolean;
  isShowColumnChooserIcon?: boolean;
  isShowRefreshButton?: boolean;
  includeGroupProperties?: boolean;
  assetsPermissions?: CRUDPermissions;
  devicesPermissions?: CRUDPermissions;
  hasCreateJob?: boolean;
  isDeviceNotes?: boolean;
  isCtcAdmin?: boolean;
  Features?: GenericObject;
  jobSearchEngineEnabled?: boolean;
  isVbuFwEnabled?: boolean;
  hasCARBEnabled?: boolean;
  hasTeleCryptEnabled?: boolean;
  includeMacAddress?: boolean;
  enableRangeSelection?: boolean;
  hasVinReadFeature?: boolean;
  setIsTaskDeviceClicked?: Function;
  onViewInVehicleManager?: (device: unknown) => void;
};

/**
 * Wrapper for eliminating the need for props when using the device
 * dialogs so we can use freely throughout ion-ctc-admin.
 */
export const DeviceActionDialogs = ({
  selectedJobId,
  isToggleSwitchGrid,
  assetsPermissions,
  hasCreateJob,
  devicesPermissions,
  Features,
  setIsTaskDeviceClicked,
  showTreeRefresh = false,
  isDeviceNotes = false,
  isVbuFwEnabled = false,
  hasCARBEnabled = false,
  hasTeleCryptEnabled = false,
  includeMacAddress = false,
  includeGroupProperties = false,
  isShowColumnChooserIcon = false,
  isShowRefreshButton = false,
  jobSearchEngineEnabled = false,
  enableRangeSelection = false,
  isCtcAdmin,
  hasVinReadFeature = false,
  onViewInVehicleManager
}: DeviceActionDialogsProps) => {
  useSignals();
  const includeSubAccounts = useSelector(devicesIncludeSubAccountsSelector);
  const totalRowsCount     = useSelector(devicesTotalRowsCountSelector);
  const search             = useSelector(devicesSearchSelector);
  const filterChips        = useSelector(devicesFiltersSelector);

  const {mappings} = useModularSearch();

  useAutoWatchCreatedJob();

  return (
    <ApplicationsDeviceActionDialogs includeSubAccounts={includeSubAccounts}
      assetsPermissions={assetsPermissions}
      includeMacAddress={includeMacAddress}
      hasTeleCryptEnabled={hasTeleCryptEnabled}
      devicesPermissions={devicesPermissions}
      hasCreateJob={hasCreateJob}
      Features={Features}
      selectedJobId={selectedJobId}
      totalRowsCount={totalRowsCount}
      isVbuFwEnabled={isVbuFwEnabled}
      hasCARBEnabled={hasCARBEnabled}
      isDeviceNotes={isDeviceNotes}
      mappings={mappings}
      search={search}
      filters={filterChips}
      setIsTaskDeviceClicked={setIsTaskDeviceClicked}
      includeGroupProperties={includeGroupProperties}
      includeDeviceGridCheckboxes
      includeAssetGridCheckboxes
      isToggleSwitchGrid={isToggleSwitchGrid}
      isShowRefreshButton={isShowRefreshButton}
      enableRangeSelection={enableRangeSelection}
      hasVinReadFeature={hasVinReadFeature}
      onViewInVehicleManager={onViewInVehicleManager}
      isCtcAdmin={isCtcAdmin}
      isShowColumnChooserIcon={isShowColumnChooserIcon}
      jobSearchEngineEnabled={jobSearchEngineEnabled}
      jobNotificationsBanner={liveJobsEnabled.value ? <CreateJobNotificationsBanner /> : undefined}
      jobNotificationsToggle={liveJobsEnabled.value ? <CreateJobNotificationsToggle /> : undefined}
      showTreeRefresh={showTreeRefresh} />
  );
};

export default memo(DeviceActionDialogs);
